import { useEffect } from "react";
import {
  useGetPersonDocuments,
  useGetPersonConsentForms,
} from "../../api/360FilesAPI";
import { useGetPersonAccounts, useGetPersonInfo } from "../../api/PersonApi";
import { useReportByUUID } from "../../api/ReportsApi";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { SummaryItem } from "../docs/Reports360SummaryEditor";
import { useGetComputedBlueprintFromWorkflow } from "../../api/BlueprintsApi";

export const useSummary360Data = (
  reportId: string | undefined,
  canViewAllReportsWithinCompany?: boolean
) => {
  const {
    data: reportData,
    isLoading: reportDataLoading,
    mutate: reportDataGet,
    error: reportDataError,
  } = useReportByUUID(canViewAllReportsWithinCompany, true);

  const { data: summaryItems, isLoading: summaryItemsLoading } =
    useCompoundResourceApi<SummaryItem>(
      `itemdal/reports/${reportId}/summaries`,
      `itemdal/summaries`
    );
  const {
    data: personEx,
    isLoading: personExLoading,
    mutate: personExGet,
  } = useGetPersonInfo();

  const {
    data: blueprint,
    mutate: getComputedBlueprint,
    isLoading: isComputedBlueprintLoading,
    isError: isComputedBlueprintError,
    isSuccess: isComputedBlueprintSuccess,
  } = useGetComputedBlueprintFromWorkflow();

  const {
    data: personAccounts,
    isLoading: personAccountsLoading,
    mutate: personAccountsGet,
  } = useGetPersonAccounts();
  const {
    data: personDocs,
    isLoading: personDocsLoading,
    mutateAsync: personDocsGet,
  } = useGetPersonDocuments();
  const { data: personConsentForms, isLoading: personConsentFormsLoading } =
    useGetPersonConsentForms(reportData?.person_uuid ?? "");

  useEffect(() => {
    if (reportData) {
      getComputedBlueprint({
        blueprintUUID: reportData.blueprint_uuid,
        companyUUID: reportData.company_uuid,
        behaviorKitUUID: reportData.behavior_kit_uuid ?? "None",
        workflowRunUUID: reportData.workflow_run_uuid,
      });
    }
  }, [
    reportData,
    getComputedBlueprint,
    reportData?.blueprint_uuid,
    reportData?.company_uuid,
    reportData?.behavior_kit_uuid,
    reportData?.workflow_run_uuid,
  ]);

  useEffect(() => {
    if (reportId) {
      reportDataGet(reportId);
    }
  }, [reportId, reportDataGet]);

  useEffect(() => {
    if (reportData) {
      personExGet(reportData.person_uuid);
      personAccountsGet(reportData.person_uuid);
      personDocsGet({
        personUuid: reportData.person_uuid,
        is_attachment: true,
      });
    }
  }, [reportData, personExGet, personAccountsGet, personDocsGet]);

  const isLoading =
    reportDataLoading ||
    personExLoading ||
    personAccountsLoading ||
    personDocsLoading ||
    personConsentFormsLoading ||
    summaryItemsLoading;

  return {
    reportData,
    reportDataError,
    personEx,
    personAccounts,
    personDocs,
    personConsentForms,
    summaryItems,
    isLoading,
    reportDataLoading,
    personExLoading,
    personAccountsLoading,
    personDocsLoading,
    summaryItemsLoading,
    blueprint,
    isComputedBlueprintLoading,
    isComputedBlueprintError,
    isComputedBlueprintSuccess,
  };
};
